import { SubVertical } from '../verticalService'
import { isPcEnv } from 'common/utils/common'

export enum Sort {
  RECENT = 'RECENT',
  POPULARITY = 'POPULARITY',
  REVIEW = 'REVIEW',
  REVIEW_SCORE = 'REVIEW_SCORE',
  DISTANCE = 'DISTANCE',
  MAP_POPULARITY = 'MAP_POPULARITY',
  LOW_PRICE = 'LOW_PRICE',
  HIGH_PRICE = 'HIGH_PRICE',
  RANK = 'RANK',
  DISCOUNT_RATE = 'DISCOUNT_RATE',
  ABC = 'ABC',
  ABC_REVERSE = 'ABC_REVERSE',
}

export enum Direction {
  ASC = 1,
  DESC = -1,
}

type SortConfig = {
  [key in Sort]?: {
    text?: {
      pc: string
      mobile: string
    }
    sortKey?: { [key in ModelName]?: { [key: string]: Direction } }
    nClick: string
  }
}

type ModelName = 'product' | 'content' | 'relationProduct' | 'channel' | 'recommendedTag' | 'coordiProduct'
type SortBySubVertical = { [key in SubVertical]?: SortConfig }

export const sortConfig: SortConfig = {
  RECENT: {
    text: {
      pc: '최신순',
      mobile: '최신',
    },
    sortKey: {
      product: { inspectedAt: Direction.DESC },
      content: { exposureAt: Direction.DESC },
      relationProduct: { regDate: Direction.DESC },
      coordiProduct: { regDate: Direction.DESC },
    },
    nClick: 'new',
  },
  POPULARITY: {
    text: {
      pc: '인기순',
      mobile: '인기',
    },
    sortKey: {
      product: { popularScore: Direction.DESC },
      content: { popularScore: Direction.DESC },
      relationProduct: { popularScoreAverage: Direction.DESC },
      coordiProduct: { popularScoreAverage: Direction.DESC },
    },
    nClick: 'hot',
  },
  REVIEW: {
    text: {
      pc: '리뷰순',
      mobile: '리뷰',
    },
    sortKey: {
      product: { totalReviewCount: Direction.DESC, _id: Direction.DESC },
    },
    nClick: 'review',
  },
  REVIEW_SCORE: {
    text: {
      pc: '평점순',
      mobile: '평점',
    },
    sortKey: {
      product: { averageReviewScore: Direction.DESC, _id: Direction.DESC },
    },
    nClick: 'good',
  },
  DISTANCE: {
    text: {
      pc: '거리순',
      mobile: '거리순',
    },
    nClick: 'near',
  },
  MAP_POPULARITY: {
    text: {
      pc: '인기순',
      mobile: '인기순',
    },
    sortKey: {
      channel: { zzimCount: Direction.DESC, _id: Direction.DESC },
    },
    nClick: 'hot',
  },
  LOW_PRICE: {
    text: {
      pc: '낮은 가격순',
      mobile: '낮은가격',
    },
    sortKey: {
      product: { [isPcEnv ? 'pcDiscountPrice' : 'mobileDiscountPrice']: Direction.ASC, _id: Direction.DESC },
    },
    nClick: 'low',
  },
  HIGH_PRICE: {
    text: {
      pc: '높은 가격순',
      mobile: '높은가격',
    },
    sortKey: {
      product: { [isPcEnv ? 'pcDiscountPrice' : 'mobileDiscountPrice']: Direction.DESC, _id: Direction.DESC },
    },
    nClick: 'high',
  },
  RANK: {
    sortKey: {
      recommendedTag: { rank: Direction.ASC, score: Direction.DESC },
    },
    nClick: '',
  },
  DISCOUNT_RATE: {
    text: {
      pc: '할인율순',
      mobile: '할인율순',
    },
    sortKey: {
      product: { [isPcEnv ? 'pcDiscountRate' : 'mobileDiscountRate']: Direction.DESC, _id: Direction.DESC },
    },
    nClick: '',
  },
  ABC: {
    text: {
      pc: '가나다순',
      mobile: '가나다순',
    },
    sortKey: {
      channel: { name: Direction.ASC },
    },
    nClick: 'abc',
  },
  ABC_REVERSE: {
    text: {
      pc: '가나다 역순',
      mobile: '가나다 역순',
    },
    sortKey: {
      channel: { name: Direction.DESC },
    },
    nClick: 'zxy',
  },
}

export const sortBySubVertical: SortBySubVertical = {
  [SubVertical.DIRECT_FARM]: {
    POPULARITY: {
      ...sortConfig.POPULARITY,
      text: {
        pc: '추천순',
        mobile: '추천',
      },
    },
  },
}

// [백아/브랜드관/디자이너/아트]	최신, 인기
// [로드샵/뷰티/리빙/키즈/펫/플레이]	최신, 인기, 리뷰
// [뷰티] 인기, 최신, 리뷰
// [쿠킹박스]	최신, 인기, 별점순
// [산지직송/지역명물] 추천, 최신, 평점, 리뷰
export const getSortCriteria = (subVertical: SubVertical): Sort[] => {
  switch (subVertical) {
    case SubVertical.DEPARTMENT:
    case SubVertical.OUTLET:
    case SubVertical.ART:
      return [Sort.RECENT, Sort.POPULARITY, Sort.LOW_PRICE, Sort.HIGH_PRICE]
    case SubVertical.STYLE:
    case SubVertical.HOME_LIVING:
    case SubVertical.HANDMADE:
    case SubVertical.STATIONERY:
    case SubVertical.KIDS:
    case SubVertical.PET:
    case SubVertical.FOREIGN:
    case SubVertical.PLAY:
      return [Sort.RECENT, Sort.POPULARITY, Sort.REVIEW]
    case SubVertical.BEAUTY:
      return [Sort.POPULARITY, Sort.RECENT, Sort.REVIEW]
    case SubVertical.HOME_MEAL:
    case SubVertical.TRADITIONAL_ALCOHOL:
      return [Sort.POPULARITY, Sort.RECENT, Sort.REVIEW_SCORE]
    case SubVertical.DIRECT_FARM:
    case SubVertical.LOCAL_FOOD:
      return [Sort.POPULARITY, Sort.RECENT, Sort.REVIEW_SCORE, Sort.REVIEW]
    default:
      return [Sort.RECENT, Sort.POPULARITY]
  }
}

export const getFirstFetchSort = (subVertical: SubVertical) => {
  if (
    [
      SubVertical.BEAUTY,
      SubVertical.HOME_MEAL,
      SubVertical.ART,
      SubVertical.TRADITIONAL_ALCOHOL,
      SubVertical.DIRECT_FARM,
      SubVertical.LOCAL_FOOD,
    ].includes(subVertical)
  ) {
    return Sort.POPULARITY
  }

  return Sort.RECENT
}

export const getSortKey = (sort: Sort, modelName: ModelName): { [key: string]: Direction } => {
  return sortConfig[sort] && sortConfig[sort].sortKey && sortConfig[sort].sortKey[modelName]
}

export const getSortNclick = (sort: Sort): string => {
  return sortConfig[sort] && sortConfig[sort].nClick
}

export const getSortText = (sort: Sort, subVertical?: SubVertical): string => {
  if (subVertical && sortBySubVertical[subVertical] && sortBySubVertical[subVertical][sort]) {
    return sortBySubVertical[subVertical][sort] && sortBySubVertical[subVertical][sort].text[process.env.PROJECT_NAME]
  }
  return sortConfig[sort] && sortConfig[sort].text && sortConfig[sort].text[process.env.PROJECT_NAME]
}
